import { TextareaInputField } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { MarginDecisionEnum } from "@/graphql/__generated__/graphql";
import { useSubmitMarginDecisionMutationHook } from "@/hooks";
import { zodResolver } from "@hookform/resolvers/zod";
import { PiArrowCounterClockwise } from "react-icons/pi";
import { JSX, useState } from "react";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { z } from "zod";
import { CircleLoader } from "@/components/shared/loaders";

const createMarginDecisionWithdrawSchema = (t: TFunction) =>
  z.object({
    decision: z.literal(MarginDecisionEnum.Reject),
    comment: z.string().min(1, `${t("margindecisionwithdrawdialog.inputs.comment.label")} ${t("schemas:form.isrequired")}`),
  });

function MarginDecisionWithdrawDialog({ hasFinalDecision }: { hasFinalDecision: boolean }): JSX.Element {
  const { t } = useTranslation(["modals", "common", "schemas"]);
  const { marginDecisionGroupID: marginDecisionGroupId } = useParams<{ marginDecisionGroupID: string; tenderID: string }>();

  const [open, setOpen] = useState(false);

  const { submitMarginDecisionMutation, loading } = useSubmitMarginDecisionMutationHook();
  const MarginDecisionWithdrawSchema = createMarginDecisionWithdrawSchema(t);
  const form = useForm<z.infer<typeof MarginDecisionWithdrawSchema>>({
    resolver: zodResolver(MarginDecisionWithdrawSchema),
    defaultValues: { decision: MarginDecisionEnum.Reject },
    mode: "onChange",
  });

  const onSubmit = (data: z.infer<typeof MarginDecisionWithdrawSchema>) => {
    submitMarginDecisionMutation({
      variables: {
        marginDecisionGroupId: Number(marginDecisionGroupId),
        decision: data.decision,
        comment: data.comment,
      },
      onCompleted: () => {
        setOpen(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-white"
          disabled={hasFinalDecision}
          onClick={() => {
            form.reset({ decision: MarginDecisionEnum.Reject });
          }}
        >
          <PiArrowCounterClockwise className="icon-xs mr-2" />
          {t("common:buttons.withdraw")}
        </Button>
      </DialogTrigger>
      <Form {...form}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("margindecisionwithdrawdialog.title")}</DialogTitle>
            <DialogDescription>
              {t("margindecisionwithdrawdialog.description")}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-col-4">
            <TextareaInputField
              form={form}
              name="comment"
              label={t("margindecisionwithdrawdialog.inputs.comment.label")}
              placeholder={t("margindecisionwithdrawdialog.inputs.comment.placeholder")}
              className="w-full"
              isFormItem={false}
            />

            <DialogFooter>
              <DialogClose asChild>
                <Button variant={"outline"}>
                  {t("common:buttons.cancel")}
                </Button>
              </DialogClose>
              <Button type="submit" disabled={loading || !form.formState.isValid} className="gap-2">
                <CircleLoader visible={loading} height={16} width={16} strokeWidth={2} />
                {t("common:buttons.withdraw")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Form>
    </Dialog>
  );
}

export default MarginDecisionWithdrawDialog;
